// src/theme.js
import { border, extendTheme, Menu } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';

// Customize the Chakra theme
const customTheme = extendTheme({
    colors: {
        brand: {
            50: '#f0f7ff',
            100: '#e0efff',
            200: '#bddaff',
            300: '#84bfff',
            400: '#4aa3ff',
            500: '#2288ff',
            600: '#0066ff',
            700: '#0052cc',
            800: '#0042a3',
            900: '#003380',
        },
        gray: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
        },
    },
    fonts: {
        heading: 'Inter, system-ui, sans-serif',
        body: 'Inter, system-ui, sans-serif',
    },
    fontSizes: {
        xs: '0.75rem',
        sm: '0.875rem',
        md: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
        '4xl': '2.25rem',
        '5xl': '3rem',
        '6xl': '3.75rem',
    },
    components: {
        Toast: {
            defaultProps: {
                position: 'top-right',
                duration: 3000,
                isClosable: true,
                variant: 'solid',
            },
            baseStyle: (props) => ({
                container: {
                    bg: props.status === 'error'
                        ? 'red.500'
                        : props.status === 'success'
                            ? 'green.500'
                            : props.status === 'warning'
                                ? 'orange.500'
                                : 'blue.500',
                    color: 'white',
                },
            }),
        },
        Tabs: {
            baseStyle: (props) => ({
                tab: {
                    _selected: {
                        color: props.colorMode === 'light' ? 'brand.600' : 'brand.200',
                        borderColor: props.colorMode === 'light' ? 'brand.600' : 'brand.200',
                        borderBottom: '2px solid',
                    },
                    _hover: {
                        color: props.colorMode === 'light' ? 'brand.600' : 'brand.200',
                    },
                    color: props.colorMode === 'light' ? 'gray.600' : 'gray.400',
                    transition: 'all 0.2s',
                },
                tablist: {
                    borderBottom: '1px solid',
                    borderColor: props.colorMode === 'light' ? 'gray.200' : 'gray.700',
                },
                tabpanel: {
                    padding: '1rem 0',
                },
            }),
            variants: {
                enclosed: (props) => ({
                    tab: {
                        _selected: {
                            color: props.colorMode === 'light' ? 'brand.600' : 'brand.200',
                            bg: props.colorMode === 'light' ? 'white' : 'gray.700',
                            borderColor: props.colorMode === 'light' ? 'gray.200' : 'gray.600',
                            borderBottom: 'none',
                            mb: '-1px',
                        },
                        borderRadius: 'md md 0 0',
                        border: '1px solid transparent',
                        bg: props.colorMode === 'light' ? 'gray.50' : 'gray.800',
                        _hover: {
                            bg: props.colorMode === 'light' ? 'gray.100' : 'gray.700',
                        },
                    },
                    tablist: {
                        borderBottom: '1px solid',
                        borderColor: props.colorMode === 'light' ? 'gray.200' : 'gray.600',
                    },
                }),
                'soft-rounded': (props) => ({
                    tab: {
                        _selected: {
                            color: 'white',
                            bg: props.colorMode === 'light' ? 'brand.500' : 'brand.400',
                        },
                        borderRadius: 'full',
                        _hover: {
                            bg: props.colorMode === 'light' ? 'gray.100' : 'gray.600',
                        },
                    },
                }),
                line: (props) => ({
                    tab: {
                        _selected: {
                            color: props.colorMode === 'light' ? 'brand.600' : 'brand.200',
                            borderColor: props.colorMode === 'light' ? 'brand.600' : 'brand.200',
                        },
                        _hover: {
                            color: props.colorMode === 'light' ? 'brand.500' : 'brand.300',
                        },
                        color: props.colorMode === 'light' ? 'gray.600' : 'gray.400',
                    },
                    tablist: {
                        borderBottom: '1px solid',
                        borderColor: props.colorMode === 'light' ? 'gray.200' : 'gray.700',
                    },
                }),
            },
        },
        Button: {
            baseStyle: {
                fontWeight: 'semibold',
                borderRadius: 'md'
            },
            variants: {
                solid: {
                    bg: 'brand.600',
                    color: 'white',
                    _hover: {
                        bg: 'brand.700',
                    },
                    _active: {
                        bg: 'brand.800',
                    },
                },
                outline: {
                    borderColor: 'brand.600',
                    color: 'brand.600',
                    _hover: {
                        bg: 'brand.50',
                        borderColor: 'brand.700',
                    },
                },
                ghost: {
                    color: 'brand.600',
                    _hover: {
                        bg: 'brand.50',
                    },
                },
            },
            defaultProps: {
                colorScheme: 'brand',
            },
        },
        Link: {
            baseStyle: {
                color: 'brand.500',
                _hover: {
                    textDecoration: 'none',
                    color: 'brand.600',
                },
            },
        },
        Card: {
            baseStyle: (props) => ({
                container: {
                    borderRadius: 'lg',
                    boxShadow: 'sm',
                    bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
                    borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
                    color: props.colorMode === 'dark' ? 'white' : 'gray.800',
                    _hover: {
                        boxShadow: 'md',
                        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
                    },
                    transition: 'all 0.2s',
                },
            }),
        },
        Text: {
            baseStyle: (props) => ({
                color: mode('gray.900', 'gray.200')(props)
            }),
        },
        Input: {
            variants: {
                outline: (props) => ({
                    field: {
                        bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
                        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
                        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
                        _hover: {
                            borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.300',
                        },
                        _focus: {
                            borderColor: 'brand.500',
                            boxShadow: `0 0 0 1px ${props.colorMode === 'dark' ? '#2288ff' : '#0066ff'}`,
                        },
                        _placeholder: {
                            color: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
                        },
                    },
                }),
            },
        },
        Select: {
            variants: {
                outline: (props) => ({
                    field: {
                        bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
                        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
                        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
                        _hover: {
                            borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.300',
                        },
                        _focus: {
                            borderColor: 'brand.500',
                            boxShadow: `0 0 0 1px ${props.colorMode === 'dark' ? '#2288ff' : '#0066ff'}`,
                        },
                    },
                }),
            },
        },
        FormLabel: {
            baseStyle: (props) => ({
                color: mode('gray.700', 'gray.200')(props)
            }),
        },
        Modal: {
            baseStyle: (props) => ({
                dialog: {
                    bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
                },
                header: {
                    color: props.colorMode === 'dark' ? 'white' : 'gray.800',
                },
                closeButton: {
                    color: props.colorMode === 'dark' ? 'gray.400' : 'gray.600',
                },
            }),
        },
        Alert: {
            variants: {
                solid: (props) => {
                    const statusColors = {
                        error: { bg: 'red.500', color: 'white', icon: 'white' },
                        success: { bg: 'green.500', color: 'white', icon: 'white' },
                        warning: { bg: 'orange.500', color: 'white', icon: 'white' },
                        info: { bg: 'blue.500', color: 'white', icon: 'white' },
                    };

                    const status = props.status || 'info';
                    const colors = statusColors[status];

                    return {
                        container: {
                            bg: colors.bg,
                            color: colors.color,
                        },
                        icon: {
                            color: colors.icon,
                        },
                        title: {
                            color: colors.color,
                        },
                        description: {
                            color: colors.color,
                        },
                    };
                },
                subtle: (props) => {
                    const statusColors = {
                        error: {
                            bg: props.colorMode === 'dark' ? 'red.900' : 'red.100',
                            color: props.colorMode === 'dark' ? 'red.200' : 'red.800',
                            icon: props.colorMode === 'dark' ? 'red.200' : 'red.600',
                        },
                        success: {
                            bg: props.colorMode === 'dark' ? 'green.900' : 'green.100',
                            color: props.colorMode === 'dark' ? 'green.200' : 'green.800',
                            icon: props.colorMode === 'dark' ? 'green.200' : 'green.600',
                        },
                        warning: {
                            bg: props.colorMode === 'dark' ? 'orange.900' : 'orange.100',
                            color: props.colorMode === 'dark' ? 'orange.200' : 'orange.800',
                            icon: props.colorMode === 'dark' ? 'orange.200' : 'orange.600',
                        },
                        info: {
                            bg: props.colorMode === 'dark' ? 'blue.900' : 'blue.100',
                            color: props.colorMode === 'dark' ? 'blue.200' : 'blue.800',
                            icon: props.colorMode === 'dark' ? 'blue.200' : 'blue.600',
                        },
                    };

                    const status = props.status || 'info';
                    const colors = statusColors[status];

                    return {
                        container: {
                            bg: colors.bg,
                            color: colors.color,
                        },
                        icon: {
                            color: colors.icon,
                        },
                        title: {
                            color: colors.color,
                        },
                        description: {
                            color: colors.color,
                        },
                    };
                },
            },
            defaultProps: {
                variant: 'solid',
            },
        },
        Menu: {
            baseStyle: (props) => ({

                list: {
                    // bg: mode('white', 'gray.800')(props),
                    borderColor: mode('gray.200', 'gray.700')(props),
                    color: mode('gray.800', 'white')(props),
                    padding: '0.5rem',
                    borderRadius: 'xl',
                },
                item: {
                    borderRadius: 'md',
                    mb: '0.2rem',
                    color: mode('gray.600', 'gray.300')(props),
                    _hover: {
                        boxShadow: 'md',
                        borderColor: mode('brand.600', 'brand.600')(props),
                        bg: mode('gray.100', 'brand.600')(props),
                        color: mode('gray.900', 'white')(props),
                        borderRadius: 'md',

                    },
                },
            }),
        },
    },
    Tooltip: {
        baseStyle: (props) => ({
            bg: mode('gray.700', 'gray.300')(props),
            color: mode('white', 'gray.800')(props),
            fontSize: 'sm',
            borderRadius: 'md',
            px: 3,
            py: 2,
            boxShadow: 'md',
        }),
    },
    styles: {
        global: (props) => ({
            body: {
                bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.100',
                color: props.colorMode === 'dark' ? 'white' : 'gray.800',
            },
            ':host,:root': {
                '--chakra-colors-chakra-body-bg': props.colorMode === 'dark' ? '#0f172a' : '#f1f5f9',
                '--chakra-colors-chakra-body-text': props.colorMode === 'dark' ? '#ffffff' : '#1e293b',
            },
        }),
    },
    config: {
        initialColorMode: 'dark',
        useSystemColorMode: false,
    },
});

export default customTheme;
